import React, { useEffect, useMemo, useRef, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { TooltipElement } from "@front-shared/elements/tooltip.element.tsx"

import { CSV_CHECKER_CONFIG } from "../../../config/csvChecker.config"
import { CsvHelper } from "../../../helpers/csv.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook.ts"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { checkerSelector } from "../../../store/selectors/checker.selector"
import type {
    ICSVUploadValidationError,
    IDynamicCheckerFormInputData,
} from "../../../types/data.types.ts"
import type {
    IContinuousMonitoringCoverageCheck,
    IMedicaidCoverageCheck,
} from "../../../types/objects.types.ts"

import trashIcon from "@front-shared/assets/images/icons/trash-red.svg"

export const SingleCsvCoverageCheckComponent: React.FunctionComponent<
    ISingleCsvCoverageCheckComponentProps
> = props => {
    const containerRef = useRef<HTMLDivElement>(null)

    const { csvCheckType } = useAppSelector(checkerSelector)

    const earlyReleaseFeaturesEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EARLY_RELEASE_FEATURES,
    )

    const [isRowHovered, setIsRowHovered] = useState<boolean>(false)
    const [actionsContainerMarginLeft, setActionsContainerMarginLeft] =
        useState<number>(0)

    const idColumnError = useMemo<ICSVUploadValidationError | undefined>(() => {
        return props.coverageCheck.inputData.csvValidationErrors?.find(
            item => item.field === "id",
        )
    }, [props.coverageCheck.inputData?.csvValidationErrors])

    useEffect(() => {
        if (!containerRef.current || !isRowHovered) {
            return
        }

        window.addEventListener("scroll", adjustActionsContainerMarginLeft)

        return () => {
            window.removeEventListener(
                "scroll",
                adjustActionsContainerMarginLeft,
            )
        }
    }, [containerRef.current, isRowHovered])

    const adjustActionsContainerMarginLeft = () => {
        if (!containerRef.current) {
            return
        }

        const boundaries = containerRef.current?.getBoundingClientRect()

        if (!boundaries) {
            return
        }

        setActionsContainerMarginLeft(boundaries.left * -1)
    }

    return (
        <div
            ref={containerRef}
            className={`
                ${props.className}
                flex group relative
            `}
            onMouseEnter={() => {
                setIsRowHovered(true)
                adjustActionsContainerMarginLeft()
            }}
            onMouseLeave={() => {
                setIsRowHovered(false)
            }}
        >
            <div className="table-row w-full">
                <div
                    className={`
                        bg-transparent
                        border-b border-solid border-brand-warmLight
                        ${
                            !props.coverageCheck.inputData.csvValidationErrors
                                ?.length
                                ? "hover:bg-brand-white"
                                : ""
                        }
                    `}
                >
                    <div className="flex items-center">
                        <div
                            className={`
                                p-[16px] mr-[16px] flex-shrink-0 w-[100px] flex items-center gap-[12px]
                                ${
                                    props.coverageCheck.inputData
                                        .csvValidationErrors?.length ||
                                    idColumnError
                                        ? "bg-brand-paleRed2"
                                        : ""
                                }
                            `}
                        >
                            <PrimaryText>
                                {props.coverageCheck.inputData.id}
                            </PrimaryText>

                            {idColumnError ? (
                                <TooltipElement
                                    text={idColumnError.errors.join("; ")}
                                    isAlert
                                />
                            ) : null}
                        </div>

                        {CSV_CHECKER_CONFIG.uploadCsvHeaderKeys[csvCheckType]
                            .filter(
                                key =>
                                    !CSV_CHECKER_CONFIG.earlyReleaseItems.fields.includes(
                                        key,
                                    ) || earlyReleaseFeaturesEligible,
                            )
                            .map((key, index) => {
                                const columnConfig =
                                    CSV_CHECKER_CONFIG.uploadCsvPreviewMapping[
                                        key
                                    ]

                                const validationError =
                                    props.coverageCheck.inputData.csvValidationErrors?.find(
                                        item => item.field === key,
                                    )

                                return columnConfig ? (
                                    <div
                                        key={index}
                                        className={`
                                            p-[16px] mr-[16px] flex-shrink-0 w-[250px] flex items-center gap-[12px]
                                            ${
                                                validationError
                                                    ? "bg-brand-paleRed2"
                                                    : ""
                                            }
                                        `}
                                    >
                                        <PrimaryText>
                                            {CsvHelper.getSingleColumnUploadPreview(
                                                columnConfig,
                                                props.coverageCheck.inputData,
                                            ) ||
                                                (validationError
                                                    ? "Empty"
                                                    : "")}
                                        </PrimaryText>

                                        {validationError ? (
                                            <TooltipElement
                                                text={validationError.errors.join(
                                                    "; ",
                                                )}
                                                isAlert
                                            />
                                        ) : null}
                                    </div>
                                ) : null
                            })}
                    </div>
                </div>

                {/*Keeping this block always to the right of row is tricky because we have horizontal scroll in table, but doable with JS*/}
                {/*we can't use position:fixed since position should be relative to row, not to window*/}
                {/*Check adjustActionsContainerMarginLeft above for details where it uses setActionsContainerMarginLeft*/}
                {/*it will update marginLeft on scroll and will keep element to the right*/}
                {/*JUST show when no results yet*/}
                <div
                    className="
                        hidden group-hover:block absolute z-10
                        h-full w-[192px] bg-brand-lilacLight top-0
                        border-l border-solid border-brand-lilac
                    "
                    style={{
                        left: "100vw",
                        transform: "translateX(-100%)",
                        marginLeft: `${actionsContainerMarginLeft}px`,
                    }}
                >
                    <div className="flex items-center h-full">
                        <div
                            className="flex-1 flex items-center justify-center cursor-pointer h-full relative"
                            onClick={() => {
                                props.onInputDataRemove(
                                    props.coverageCheck.inputData,
                                )
                            }}
                        >
                            <div className="absolute screen-center">
                                <img
                                    className="mx-auto mt-[10px]"
                                    src={trashIcon}
                                    alt="Remove"
                                />
                                <PrimaryText centered size={10}>
                                    Remove
                                </PrimaryText>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

interface ISingleCsvCoverageCheckComponentProps {
    className?: string
    rowIndex: number
    coverageCheck: IContinuousMonitoringCoverageCheck | IMedicaidCoverageCheck
    onInputDataRemove: (inputData: IDynamicCheckerFormInputData) => void
}
